import {
    Box,
    Checkbox,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    styled,
} from '@mui/material';
import NumberInput from '~/components/Inputs/NumberInput';
import useDoseTargetData from '~/hooks/useDoseTargetData';

type DoseStructuresTableProps = {
    isLoading?: boolean;
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        cursor: 'pointer',
    },
    '&:hover .hover-add-dose': {
        display: 'inline-flex',
    },
}));

const TableLoader = () => (
    <TableRow>
        <TableCell colSpan={3} align="center" height={80}>
            <CircularProgress size={24} />
            <Typography variant="body2" mt={0.5}>
                Loading datasets...
            </Typography>
        </TableCell>
    </TableRow>
);

const DoseStructuresTable = ({ isLoading }: DoseStructuresTableProps) => {
    const { doseTargetData, handleTargetChange, handleDoseQuantityChange } =
        useDoseTargetData();

    return (
        <TableContainer
            sx={(theme) => ({
                height: '240px',
                overflowY: 'auto',
                borderRadius: `${theme.shape.borderRadius}px`,
                border: `1px solid ${theme.palette.divider}`,
            })}
        >
            <Table stickyHeader size="small">
                <TableHead
                    sx={(theme) => ({
                        background: theme.palette.background.default,
                    })}
                >
                    <TableRow>
                        <TableCell
                            align="left"
                            sx={{ width: '50%', paddingLeft: 3 }}
                        >
                            TARGET ROI
                        </TableCell>
                        <TableCell align="left" sx={{ width: '20%' }}>
                            TYPE
                        </TableCell>
                        <TableCell align="right" sx={{ width: '30%' }}>
                            DOSE (Gy)
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isLoading ? (
                        <TableLoader />
                    ) : (
                        doseTargetData.map((structure, i) => (
                            <StyledTableRow key={i}>
                                <TableCell
                                    align="left"
                                    sx={{
                                        paddingLeft: 0,
                                    }}
                                >
                                    <Checkbox
                                        data-testid={`doseROI${i}`}
                                        size="small"
                                        checked={structure.isTarget}
                                        onClick={() => handleTargetChange(i)}
                                    />
                                    {structure.name}
                                </TableCell>
                                <TableCell align="left">
                                    {structure.interpretedType}
                                </TableCell>
                                <TableCell align="right">
                                    {structure.isTarget ? (
                                        <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <NumberInput
                                                autoFocus
                                                value={structure.doseQuantity}
                                                min={0}
                                                max={100}
                                                step={1}
                                                onChange={(e) =>
                                                    handleDoseQuantityChange(
                                                        i,
                                                        e
                                                    )
                                                }
                                                inputProps={{
                                                    style: {
                                                        textAlign: 'left',
                                                        width: '50px',
                                                        fontSize: 12,
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <>
                                            <Typography
                                                className="hover-add-dose"
                                                display="none"
                                                color="primary"
                                                fontSize={12}
                                                onClick={() =>
                                                    handleTargetChange(i)
                                                }
                                            >
                                                Select as target
                                            </Typography>
                                        </>
                                    )}
                                </TableCell>
                            </StyledTableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default DoseStructuresTable;
